import React, { useEffect, useState } from "react";
import { Sparkles, Save, FileText, CheckCircle2, Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { api, aiJob } from "@/lib/api";
import { PageHeader, SectionCard, EmptyState } from "@/components/Primitives";
import { ClinicalBanner, AIOutputDisclaimer } from "@/components/Disclaimer";
import ShowReasoning from "@/components/ShowReasoning";
import AiAnswerCard from "@/components/AiAnswerCard";
import { exportToPDF } from "@/lib/pdf";

export default function Charting() {
  const [patients, setPatients] = useState([]);
  const [patientId, setPatientId] = useState("");
  const [freeText, setFreeText] = useState("");
  const [soap, setSoap] = useState(null);
  const [score, setScore] = useState(0);
  const [missing, setMissing] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reasoning, setReasoning] = useState(null);
  const [verificationFooter, setVerificationFooter] = useState("");

  useEffect(() => {
    api.get("/patients").then((r) => {
      setPatients(r.data);
      if (r.data[0]) setPatientId(r.data[0].id);
    });
  }, []);

  const generate = async () => {
    if (!freeText.trim() || !patientId) return toast.error("Pick a patient and add notes first");
    setAiLoading(true);
    try {
      const data = await aiJob("/ai/soap", { patient_id: patientId, free_text: freeText });
      // The result.final is a JSON string per topology - try parse
      let parsed = null;
      try { parsed = JSON.parse(data.final); } catch {
        try { parsed = JSON.parse(data.draft); } catch { parsed = null; }
      }
      if (parsed) {
        setSoap(parsed);
        setScore(parsed.completeness_score || data.score || 80);
        setMissing(parsed.missing_elements || []);
      } else {
        setSoap({ raw: data.final || data.draft });
        setScore(data.score || 80);
      }
      setReasoning(data.reasoning || null);
      setVerificationFooter(data.verification_footer || "This output requires independent clinical verification before use.");
      toast.success("SOAP note drafted by Topology AI");
    } catch (e) {
      toast.error("AI generation failed: " + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  const saveAndSign = async (sign = false) => {
    if (!soap) return;
    setSaving(true);
    try {
      const payload = {
        patient_id: patientId,
        notes: freeText,
        soap,
        cpt_codes: soap.cpt_codes || [],
        icd10_codes: soap.icd10_codes || [],
        completeness_score: score,
        status: sign ? "signed" : "draft",
      };
      const { data } = await api.post("/encounters", payload);
      if (sign) await api.post(`/encounters/${data.id}/sign`);
      toast.success(sign ? "Encounter signed" : "Encounter saved as draft");
      setSoap(null); setFreeText(""); setScore(0); setMissing([]);
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const exportPdf = () => {
    if (!soap) return;
    const text = soap.raw ?
      soap.raw :
      `SUBJECTIVE\n${soap.subjective}\n\nOBJECTIVE\n${soap.objective}\n\nASSESSMENT\n${soap.assessment}\n\nPLAN\n${soap.plan}\n\nCPT: ${(soap.cpt_codes||[]).map(c=>`${c.code} ${c.description}`).join(", ")}\nICD-10: ${(soap.icd10_codes||[]).map(c=>`${c.code} ${c.description}`).join(", ")}`;
    exportToPDF("SOAP Note", text);
  };

  const scoreColor = score >= 85 ? "text-emerald-600" : score >= 70 ? "text-amber-600" : "text-red-600";

  return (
    <>
      <PageHeader eyebrow="Module 1" title="AI Charting Engine"
        subtitle="Dictate or type your encounter. The adaptive topology drafts a SOAP note, suggests CPT/ICD-10 codes, and scores completeness before signing."
        testid="charting-page" />
      <ClinicalBanner />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <SectionCard title="Encounter Notes" subtitle="Free-text dictation" testid="encounter-input">
            <select value={patientId} onChange={(e)=>setPatientId(e.target.value)}
              className="w-full mb-3 px-3 py-2.5 bg-white border border-brand rounded-md text-sm"
              data-testid="patient-select">
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name} • {p.insurance_plan}</option>)}
            </select>
            <textarea value={freeText} onChange={(e)=>setFreeText(e.target.value)}
              rows={14} placeholder="Patient is a 56-year-old female presenting with..."
              className="w-full px-3 py-3 bg-white border border-brand rounded-md text-sm"
              data-testid="free-text-input" />
            <div className="flex gap-2 mt-3">
              <button onClick={generate} disabled={aiLoading}
                className="bg-brand-primary text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#35096B] inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="generate-soap-btn">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate SOAP
              </button>
            </div>
          </SectionCard>
        </div>

        <div className="lg:col-span-2">
          <SectionCard title="Documentation Score" testid="score-card">
            <div className="flex items-center justify-between">
              <div>
                <div className={`text-5xl font-semibold tracking-tight ${scoreColor}`} data-testid="score-value">{score}%</div>
                <div className="text-xs text-brand-muted mt-1">Completeness</div>
              </div>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center ${score>=85?"bg-emerald-50":score>=70?"bg-amber-50":"bg-red-50"}`}>
                <FileText className={`w-7 h-7 ${scoreColor}`} strokeWidth={1.5} />
              </div>
            </div>
            {missing.length > 0 && (
              <div className="mt-4 pt-4 border-t border-brand">
                <div className="eyebrow mb-2">Missing Elements</div>
                <ul className="text-xs text-brand-muted space-y-1">
                  {missing.map((m, i) => <li key={i}>• {m}</li>)}
                </ul>
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {soap && (
        <SectionCard title="Drafted SOAP Note"
          actions={
            <div className="flex gap-2">
              <button onClick={exportPdf} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5" data-testid="export-pdf-btn">
                <Download className="w-3.5 h-3.5" /> Export PDF
              </button>
              <button onClick={() => saveAndSign(false)} disabled={saving} className="text-xs px-3 py-1.5 rounded-md border border-brand hover:bg-brand-bg inline-flex items-center gap-1.5" data-testid="save-draft-btn">
                <Save className="w-3.5 h-3.5" /> Save Draft
              </button>
              <button onClick={() => saveAndSign(true)} disabled={saving || score < 70}
                className="text-xs px-3 py-1.5 rounded-md bg-brand-teal text-white hover:bg-emerald-700 inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="sign-btn">
                <CheckCircle2 className="w-3.5 h-3.5" /> Sign Encounter
              </button>
            </div>
          }
          testid="soap-result">
          <AIOutputDisclaimer />
          {soap.raw ? (
            <pre className="text-sm whitespace-pre-wrap leading-relaxed text-brand-text">{soap.raw}</pre>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
              <Section label="Subjective" body={soap.subjective} />
              <Section label="Objective" body={soap.objective} />
              <Section label="Assessment" body={soap.assessment} />
              <Section label="Plan" body={soap.plan} />
              <CodeBlock label="CPT Codes" codes={soap.cpt_codes} />
              <CodeBlock label="ICD-10 Codes" codes={soap.icd10_codes} />
            </div>
          )}
          {verificationFooter && (
            <div className="mt-4 px-3 py-2 rounded-md bg-amber-50 border border-amber-300 text-xs text-amber-900 italic" data-testid="soap-verification-footer">
              {verificationFooter}
            </div>
          )}
          <ShowReasoning reasoning={reasoning} score={score} testid="soap-reasoning" />
          <AiAnswerCard
            platformModule="charting"
            originalQuery={freeText}
            originalAiResponse={soap ? `S: ${soap.subjective}\n\nO: ${soap.objective}\n\nA: ${soap.assessment}\n\nP: ${soap.plan}` : ""}
            retrievedEntryIds={soap?.retrieved_entry_ids || []}
            suggestedDomain="charting"
            testid="soap-answer-card"
          />
        </SectionCard>
      )}
      {!soap && <div className="mt-6"><EmptyState icon={Sparkles} title="No draft yet" hint="Dictate above, then Generate SOAP." /></div>}
    </>
  );
}

function Section({ label, body }) {
  return (
    <div>
      <div className="eyebrow mb-1.5">{label}</div>
      <div className="text-sm text-brand-text leading-relaxed">{body || "—"}</div>
    </div>
  );
}

function CodeBlock({ label, codes }) {
  return (
    <div>
      <div className="eyebrow mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {(codes || []).map((c, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-brand-bg border border-brand text-xs">
            <span className="font-mono font-semibold text-brand-primary">{c.code}</span>
            <span className="text-brand-muted">{c.description}</span>
          </span>
        ))}
        {(codes || []).length === 0 && <span className="text-xs text-brand-muted">None suggested</span>}
      </div>
    </div>
  );
}
